/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Outlet, useLocation, useParams } from "react-router-dom";
import {
  type ChildrenProp,
  NavigationActionInterceptorProvider,
  type Timeout,
  useDebug,
  useDelayedState,
  useLocalState,
  useMaybePubSub,
} from "@huub-web/ui";

import { AppLayout } from "../AppLayout";
import {
  AppLayoutContent,
  LAYOUT_CONTENT_SCROLL_EMIT_TAG,
} from "../AppLayoutContent";
import {
  NavigationStateProvider,
  type NavigationStateProviderProps,
} from "../../providers";
import {
  AuthMiddleware,
  useAPI,
  useAuth,
  useWebSocket,
  WebSocketChannel,
} from "@huub-web/backend";
import { rem, throttle } from "@huub-web/utils";
import { useFunnelCookie } from "@huub-web/shared";
import { useTemplates } from "@huub-web/templating";

import { BasePage } from "../BasePage/BasePage";
import { LoadingPage } from "../LoadingPage/LoadingPage";
import { ErrorPage } from "../ErrorPage/ErrorPage";
import { Navigation } from "../Navigation";
import { SideAgent } from "../SideAgent/SideAgent";
import { TaskBarProvider } from "../TaskBarProvider/TaskBarProvider";
import { TaskBarController } from "../TaskBarController/TaskBarController";
import { Header } from "../Header/Header";
import { TranscriptListener } from "../TranscriptListener/TranscriptListener";
import { NotFoundScreen } from "../NotFoundScreen/NotFoundScreen";
import { ChatHistoryProvider } from "../ChatHistoryProvider/ChatHistoryProvider";
import {
  useNavigate,
  useAppController,
  useAppSettings,
  useCompactLayout,
  useTaskBar,
} from "../../hooks";
import {
  DEFAULT_LAYOUT_CONFIG,
  type LayoutConfig,
  type PageOutletContext,
} from "../../hooks/usePageReady";
import { FUNNEL_DATA_KEY, type FunnelData } from "../../types/FunnelData";

import { pageHiddenStyle, pageTransitionStyle } from "./AppPageLayout.css";

/**
 * Persistent layout that stays mounted across route changes.
 * Contains navigation, header, side panel, taskbar, and all shared logic
 * (chat initiation, funnel handling, 404 state). The route-specific screen
 * content is rendered via `<Outlet />` with a CSS fade-in animation.
 */
export const AppPageLayout: FC<ChildrenProp> = ({ children }) => {
  const pubSub = useMaybePubSub();
  const { log } = useDebug();
  const { isConnected, sendMessage } = useWebSocket();
  const {
    navigationState,
    clearChat,
    isAppStateLoading,
    errorLoadingAppState,
    isLoading,
  } = useAppController();
  const { navigate, location } = useNavigate();
  const { isVisualDebugEnabled } = useAppSettings();
  const { $post } = useAPI();
  const { isUnauthorized } = useAuth();
  const { setNavigationContext, setChatOpen } = useTemplates();
  const params = useParams();
  const routerLocation = useLocation();

  const [sideContentOpen, setSideContentOpen] = useState(false);
  const sideContentOpenRef = useRef(false);
  const [showNotFound, setShowNotFound] = useState(false);
  const [isRouteTransitioning, setIsRouteTransitioning] = useState(false);
  const [isDelayedLoading, setDelayedLoading, setImmediateLoading] =
    useDelayedState(false, 1000);

  /**
   * Tracks the current navigation context locally so we can derive
   * a stable animation key. Updated via `handleNavigationContextChange`.
   */
  const [localNavigationContext, setLocalNavigationContext] = useState<
    string[]
  >([]);
  const prevTopLevelContextRef = useRef<string | null>(null);

  /**
   * When the top-level record has `innerRouting: true`, use the parent
   * slug as the React key instead of the full pathname. This keeps the
   * DOM element stable across sub-route navigations, skipping the
   * fade-in animation and content-ready reset.
   */
  const animationKey = (() => {
    if (!navigationState) return routerLocation.pathname;

    const topLevelId = localNavigationContext[0];
    const topLevelRecord = navigationState.find((r) => r.id === topLevelId);

    if (topLevelRecord?.innerRouting) {
      return `/${topLevelRecord.slug}`;
    }

    return routerLocation.pathname;
  })();

  /**
   * Set to `true` when the active screen calls `onLoadComplete` via
   * the outlet context. Reset when the animation key changes.
   */
  const [isContentReady, setIsContentReady] = useState(false);
  const isPageReady = isContentReady;

  const [layoutConfig, setLayoutConfigState] = useState(DEFAULT_LAYOUT_CONFIG);

  // Memoized because it's passed through to `<Outlet context={...}>` and the
  // consuming screens (e.g. AppScreen) use `onLoadComplete` in a useEffect
  // dependency array. A fresh object each render would re-fire the effect and
  // flip isContentReady prematurely, interfering with the page-transition
  // animation. useState setters are stable, so empty deps are safe.
  const outletContext = useMemo(
    () => ({
      onLoadComplete: () => setIsContentReady(true),
      setLayoutConfig: (config: Partial<LayoutConfig>) =>
        setLayoutConfigState((prev) => {
          const next = { ...prev, ...config };
          if (
            prev.header === next.header &&
            prev.sideAgent === next.sideAgent &&
            prev.headerActions === next.headerActions &&
            prev.fullBleed === next.fullBleed
          ) {
            return prev;
          }
          return next;
        }),
    }),
    [],
  );

  /**
   * Reset content-ready and start the route transition when the animation
   * key changes. Uses the "setState during render" pattern so the reset
   * happens synchronously before effects, preventing child `onLoadComplete`
   * calls from being overwritten by a later parent effect.
   */
  const [prevAnimationKey, setPrevAnimationKey] = useState(animationKey);

  if (animationKey !== prevAnimationKey) {
    setPrevAnimationKey(animationKey);
    setIsContentReady(false);
    setIsRouteTransitioning(true);
    // Clear stale navigation context from the previous page so contextless
    // pages don't inherit the old context.
    setLocalNavigationContext([]);
    setLayoutConfigState(DEFAULT_LAYOUT_CONFIG);
  }

  useEffect(() => {
    if (!isRouteTransitioning) return;
    const timeout = setTimeout(() => setIsRouteTransitioning(false), 300);
    return () => clearTimeout(timeout);
  }, [isRouteTransitioning, prevAnimationKey]);

  useEffect(() => {
    const loading = isLoading || isRouteTransitioning;
    if (loading) {
      setImmediateLoading(true);
    } else {
      setDelayedLoading(false);
    }
  }, [isLoading, isRouteTransitioning]);

  useEffect(() => {
    if (isContentReady) return;
    const safety = setTimeout(() => setIsContentReady(true), 3000);
    return () => clearTimeout(safety);
  }, [isContentReady, animationKey]);

  // Active conversation ID
  // Derived from URL params instead of screenType prop
  // -------------------

  const dynamicID = params.id;

  const isHistoryRoute = localNavigationContext[0] === "chatHistory";
  const activeConversationID = isHistoryRoute && dynamicID ? dynamicID : null;

  // Chat initiate
  // -------------------

  const skipNextChatInitiateRef = useRef(false);
  const chatInitiateState = useRef<string[] | null>(null);
  const chatInitiateTimeout = useRef<Timeout | null>(null);
  const lastChatInitiateState = useRef<string[] | null>(null);

  const initiateChat = useCallback(
    (state: string[]) => {
      log(
        "green",
        "AppPageLayout",
        "initiateChat",
        state,
        sideContentOpenRef.current,
      );

      if (skipNextChatInitiateRef.current && chatInitiateState.current) {
        log(
          "green",
          "AppPageLayout",
          "Initiate chat skipped, because of onPrompt",
          chatInitiateState.current,
        );

        lastChatInitiateState.current = chatInitiateState.current;
        chatInitiateState.current = null;
        skipNextChatInitiateRef.current = false;
        return;
      }

      if (!sideContentOpenRef.current) {
        chatInitiateState.current = state;
        return;
      }

      if (
        lastChatInitiateState.current &&
        lastChatInitiateState.current.join(".") === state.join(".")
      ) {
        return;
      }

      if (["chatHistory"].includes(state?.[0])) {
        if (chatInitiateTimeout.current) {
          clearTimeout(chatInitiateTimeout.current);
        }
        clearChat();
        lastChatInitiateState.current = state;
        return;
      }

      if (chatInitiateTimeout.current) {
        clearTimeout(chatInitiateTimeout.current);
      }

      chatInitiateTimeout.current = setTimeout(() => {
        clearChat();
        pubSub?.publish("manualWaitingForChatAnswer", {});
        sendMessage(WebSocketChannel.CHAT_INITIATE, { context: state });
        chatInitiateState.current = null;
        lastChatInitiateState.current = state;
      }, 500);
    },
    [clearChat, pubSub, sendMessage],
  );

  const initiateChatThrottled = useRef(throttle(initiateChat, 100));
  useEffect(() => {
    initiateChatThrottled.current = throttle(initiateChat, 100);
  }, [initiateChat]);

  useEffect(() => {
    sideContentOpenRef.current = sideContentOpen;

    if (sideContentOpen && chatInitiateState.current) {
      log(
        "green",
        ["AppPageLayout", "useEffect"],
        "initiateChatThrottled",
        chatInitiateState.current,
      );
      initiateChatThrottled.current(chatInitiateState.current);
    }
  }, [sideContentOpen]);

  useEffect(() => {
    setChatOpen?.(sideContentOpen);
  }, [sideContentOpen, setChatOpen]);

  useEffect(() => {
    // Always send to the backend, including empty arrays — this ensures
    // stale navigation context is cleared when navigating to contextless pages.
    setNavigationContext?.(localNavigationContext);
  }, [localNavigationContext, setNavigationContext]);

  // User prompt
  // -------------------

  useEffect(() => {
    if (!pubSub) {
      return;
    }

    const unsubscribePrompt = pubSub.subscribe("onPrompt", ({ value }) => {
      log("green", "AppPageLayout", "onPrompt:", value);

      if (!sideContentOpenRef.current) {
        skipNextChatInitiateRef.current = true;
      }
    });

    return () => unsubscribePrompt();
  }, [pubSub]);

  // Stop all
  // -------------------

  useEffect(() => {
    const unsubscribe = pubSub?.subscribe("onStopAll", () => {
      if (chatInitiateTimeout.current) {
        clearTimeout(chatInitiateTimeout.current);
      }
    });

    return () => unsubscribe?.();
  }, []);

  // Navigation
  // -------------------

  const handleNavigationContextChange: NavigationStateProviderProps["onNavigationContextChange"] =
    (context) => {
      log("green", "AppPageLayout", "handleNavigationContextChange", context);

      setLocalNavigationContext(context ?? []);

      const topLevelId = context?.[0] ?? null;
      const topLevelRecord = navigationState?.find((r) => r.id === topLevelId);
      const isInnerNavigation =
        topLevelRecord?.innerRouting &&
        prevTopLevelContextRef.current === topLevelId;

      prevTopLevelContextRef.current = topLevelId;

      setShowNotFound(false);

      if (!isInnerNavigation) {
        pubSub?.publish("scrollToTop", { tag: LAYOUT_CONTENT_SCROLL_EMIT_TAG });
      }

      if (isConnected && context && context.length > 0) {
        initiateChat(context);
      } else {
        chatInitiateState.current =
          context && context.length > 0 ? context : null;
      }
    };

  const handleManualNavigation: NavigationStateProviderProps["onManualNavigation"] =
    (navigationCtx, url) => {
      log(
        "green",
        "AppPageLayout",
        "handleManualNavigation",
        navigationCtx,
        url,
      );

      if (url) {
        const urlWithoutHash = url.split("#")[0];
        navigate(urlWithoutHash);
      }
    };

  // Path not found
  // -------------------

  const handlePathNotFound: NavigationStateProviderProps["onPathNotFound"] = (
    path,
  ) => {
    if (import.meta.env.DEV && path.startsWith("/dev/")) {
      return;
    }

    // Only suppress "not found" for the two known interior-designer route shapes:
    //   /:section/interior-designer
    //   /:section/:property/interior-designer
    if (/^\/?([^/]+\/){1,2}interior-designer$/.test(path)) {
      return;
    }

    setShowNotFound(true);
    if (isVisualDebugEnabled) {
      pubSub?.publish("snackbar", {
        body: `Path not found: *${path}*`,
        variant: "error",
        autoHide: false,
      });
    }
  };

  const handle404ButtonClick = () => {
    setShowNotFound(false);
    pubSub?.publish("navigate", { context: ["hub"] });
  };

  // Check funnel
  // -------------

  const [funnelData, setFunnelData] = useLocalState<FunnelData>(
    FUNNEL_DATA_KEY,
    { __followUp: false },
  );
  const { getFunnelCookie, deleteFunnelCookie } = useFunnelCookie({
    cookieName: import.meta.env.VITE_FUNNEL_COOKIE_NAME,
    cookieDomain: import.meta.env.VITE_AUTH_COOKIE_DOMAIN,
  });

  const funnelRedirectTimeoutRef = useRef<Timeout | null>(null);
  const isFunnelInitiatedRef = useRef(false);
  const isCookieCheckedRef = useRef(false);

  useEffect(() => {
    if (isCookieCheckedRef.current) {
      return;
    }

    isCookieCheckedRef.current = true;

    const cookieData = getFunnelCookie();
    if (cookieData) {
      log("green", "AppPageLayout", "Found funnel cookie", cookieData);

      setFunnelData({
        ...cookieData,
        addressObjectID: cookieData.addressObjectID ?? null,
        __followUp: true,
      });

      deleteFunnelCookie();
    }
  }, [setFunnelData, getFunnelCookie, deleteFunnelCookie]);

  useEffect(() => {
    if (!funnelData || !pubSub || isFunnelInitiatedRef.current) {
      return;
    }

    if (funnelData.__followUp) {
      isFunnelInitiatedRef.current = true;

      log("green", "AppPageLayout", "Following up on funnelData", funnelData);

      if (funnelData.addressObjectID) {
        if (funnelRedirectTimeoutRef.current) {
          clearTimeout(funnelRedirectTimeoutRef.current);
        }

        funnelRedirectTimeoutRef.current = setTimeout(() => {
          log(
            "green",
            "AppPageLayout",
            "Navigating to addressObjectID",
            funnelData.addressObjectID,
          );

          if (funnelData.addressObjectID) {
            pubSub.publish("navigate", {
              context: ["orientation", funnelData.addressObjectID],
            });
          }
        }, 1000);
      }

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { __followUp, ...funnelDataToSend } = funnelData;
      $post("/frontend/funnel", { ...funnelDataToSend });

      setFunnelData({
        ...funnelData,
        __followUp: false,
      });
    }

    return () => {
      if (funnelRedirectTimeoutRef.current) {
        clearTimeout(funnelRedirectTimeoutRef.current);
      }
    };
  }, [funnelData, pubSub]);

  return (
    <ChatHistoryProvider activeConversationID={activeConversationID}>
      <AuthMiddleware />

      {(isAppStateLoading || isUnauthorized) && <LoadingPage />}

      {!isAppStateLoading && !isUnauthorized && errorLoadingAppState && (
        <ErrorPage />
      )}

      {navigationState &&
        !isAppStateLoading &&
        !isUnauthorized &&
        !errorLoadingAppState && (
          <BasePage>
            <NavigationActionInterceptorProvider>
              <NavigationStateProvider
                navigationState={navigationState}
                currentPath={location?.pathname}
                onNavigationContextChange={handleNavigationContextChange}
                onManualNavigation={handleManualNavigation}
                onPathNotFound={handlePathNotFound}
              >
                <TranscriptListener />

                <TaskBarProvider>
                  <AppPageLayoutInner
                    loading={isDelayedLoading}
                    layoutConfig={layoutConfig}
                    sideContentOpen={sideContentOpen}
                    onSideContentOpenChange={setSideContentOpen}
                    showNotFound={showNotFound}
                    onNotFoundButtonClick={handle404ButtonClick}
                    animationKey={animationKey}
                    pageReady={isPageReady}
                    outletContext={outletContext}
                  >
                    {children}
                  </AppPageLayoutInner>
                </TaskBarProvider>
              </NavigationStateProvider>
            </NavigationActionInterceptorProvider>
          </BasePage>
        )}
    </ChatHistoryProvider>
  );
};

type AppPageLayoutInnerProps = ChildrenProp & {
  loading: boolean;
  layoutConfig: LayoutConfig;
  sideContentOpen: boolean;
  onSideContentOpenChange: (open: boolean) => void;
  showNotFound: boolean;
  onNotFoundButtonClick: () => void;
  animationKey: string;
  pageReady: boolean;
  outletContext: PageOutletContext;
};

/**
 * Inner layout rendered inside NavigationStateProvider so it can
 * access viewport-based layout variants via `useCompactLayout`.
 */
const AppPageLayoutInner: FC<AppPageLayoutInnerProps> = ({
  loading,
  layoutConfig,
  sideContentOpen,
  onSideContentOpenChange,
  showNotFound,
  onNotFoundButtonClick,
  animationKey,
  pageReady,
  outletContext,
  children,
}) => {
  const pubSub = useMaybePubSub();
  const { isCompact } = useCompactLayout();
  const { hasCanvasTaskBar } = useTaskBar();

  return (
    <AppLayout
      loading={loading}
      fullBleed={layoutConfig.fullBleed}
      navigation={<Navigation />}
      taskBar={
        isCompact && sideContentOpen ? (
          <TaskBarController
            history
            onHistoryClick={() => {
              pubSub?.publish("sideContent", {
                action: "open",
                id: "history",
              });
            }}
          />
        ) : null
      }
      trailing={
        layoutConfig.sideAgent ? (
          <SideAgent onOpenChange={(open) => onSideContentOpenChange(open)} />
        ) : null
      }
    >
      <AppLayoutContent
        hasTaskBar={hasCanvasTaskBar}
        leading={
          layoutConfig.header ? (
            <Header headerActions={layoutConfig.headerActions} />
          ) : undefined
        }
        trailing={
          isCompact && sideContentOpen ? (
            <div style={{ height: rem(100) }} />
          ) : null
        }
      >
        {showNotFound ? (
          <NotFoundScreen onGoHome={onNotFoundButtonClick} />
        ) : (
          <div
            key={animationKey}
            className={pageReady ? pageTransitionStyle : pageHiddenStyle}
          >
            <Outlet context={outletContext} />
          </div>
        )}
        {children}
      </AppLayoutContent>
    </AppLayout>
  );
};
